import {
  BOOKING_REORDERING_COHORT,
  type BookingReorderingCohort,
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "@src/appV2/redesign/PlacementCandidate/types";

interface UseBookingCohortEligibilityParams {
  bookingReorderingCohort: BookingReorderingCohort;
  placementCandidate: Pick<PlacementCandidate, "screeningStatus">;
  isRecruiterScreeningEnabled: boolean;
}

/**
 * Determines eligibility for booking cohort modals.
 * Checks if worker should see screen_last screening modal or book_first confirmation.
 */
export function useBookingCohortEligibility(params: UseBookingCohortEligibilityParams) {
  const { bookingReorderingCohort, placementCandidate, isRecruiterScreeningEnabled } = params;

  const hasNotPassedScreening = !placementCandidate.screeningStatus?.startsWith(
    PlacementCandidateScreeningStatus.PASSED
  );
  const isNotPendingScreening =
    placementCandidate.screeningStatus !== PlacementCandidateScreeningStatus.PENDING;

  // Check if eligible to show screen_last screening modal (excludes runtime modal type check)
  // Note: showCallMeNow is not checked here - the modal should still show outside of call hours
  // with just the Calendly scheduling option available
  const isEligibleForScreenLastScreeningModal =
    bookingReorderingCohort === BOOKING_REORDERING_COHORT.SCREEN_LAST &&
    hasNotPassedScreening &&
    isNotPendingScreening &&
    isRecruiterScreeningEnabled;

  // Check if should show BookFirstInterviewConfirmationDialog instead of InterviewBookedDialog
  const isEligibleForBookFirstConfirmation =
    bookingReorderingCohort === BOOKING_REORDERING_COHORT.BOOK_FIRST &&
    hasNotPassedScreening &&
    isRecruiterScreeningEnabled;

  return {
    isEligibleForScreenLastScreeningModal,
    isEligibleForBookFirstConfirmation,
  };
}

export type BookingCohortEligibilityReturn = ReturnType<typeof useBookingCohortEligibility>;
